import { useState, useEffect, useCallback } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Pin } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Mirrors the sticky palette in ClipboardModal.jsx
const COLOR_CLASSES = {
  yellow: { bg: '#fde047', edge: 'rgba(120, 80, 0, 0.08)', text: 'text-yellow-950', accent: 'text-yellow-900/70', placeholder: 'placeholder:text-yellow-950/55' },
  pink: { bg: '#f9a8d4', edge: 'rgba(150, 30, 80, 0.10)', text: 'text-pink-950', accent: 'text-pink-900/70', placeholder: 'placeholder:text-pink-950/55' },
  blue: { bg: '#7dd3fc', edge: 'rgba(20, 60, 150, 0.10)', text: 'text-sky-950', accent: 'text-sky-900/70', placeholder: 'placeholder:text-sky-950/55' },
  green: { bg: '#86efac', edge: 'rgba(20, 100, 50, 0.10)', text: 'text-emerald-950', accent: 'text-emerald-900/70', placeholder: 'placeholder:text-emerald-950/55' },
  purple: { bg: '#c4b5fd', edge: 'rgba(70, 40, 150, 0.10)', text: 'text-purple-950', accent: 'text-purple-900/70', placeholder: 'placeholder:text-purple-950/55' },
  orange: { bg: '#fdba74', edge: 'rgba(150, 80, 20, 0.10)', text: 'text-orange-950', accent: 'text-orange-900/70', placeholder: 'placeholder:text-orange-950/55' },
};

export function usePinnedSticky() {
  const [stickies, setStickies] = useState([]);
  const [pref, setPref] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [clip, pin] = await Promise.all([
        api.get('/clipboard'),
        api.get('/clipboard/dashboard-pin'),
      ]);
      const all = [...(clip.data.board || []), ...(clip.data.stack || [])].sort((a, b) => a.slot - b.slot);
      setStickies(all);
      setPref(pin.data.sticky_id || null);
    } catch { /* clipboard unavailable - dashboard renders without sticky */ }
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const sticky = !loaded ? null
    : pref ? (stickies.find(s => s.id === pref) || null)
      : (stickies.find(s => (s.content || '').trim()) || null);

  const setPin = async (sid) => {
    try {
      await api.put('/clipboard/dashboard-pin', { sticky_id: sid });
      setPref(sid);
    } catch { toast.error('Could not update pinned note'); }
  };

  const saveContent = async (sid, content) => {
    try {
      const r = await api.put(`/clipboard/${sid}`, { content });
      setStickies(prev => prev.map(s => (s.id === sid ? { ...s, ...r.data } : s)));
    } catch { toast.error('Could not save note'); }
  };

  return { sticky, stickies, setPin, saveContent };
}

export function PinnedStickyCard({ sticky, stickies, onSelect, onSave }) {
  const [draft, setDraft] = useState(sticky.content || '');
  const [syncedId, setSyncedId] = useState(sticky.id);
  if (sticky.id !== syncedId) {
    setSyncedId(sticky.id);
    setDraft(sticky.content || '');
  }
  const c = COLOR_CLASSES[sticky.color] || COLOR_CLASSES.yellow;

  return (
    <div
      className="relative rounded-md overflow-hidden col-span-2 md:col-span-1 shadow-sm"
      style={{
        backgroundColor: c.bg,
        backgroundImage: `linear-gradient(180deg, ${c.edge} 0%, ${c.edge} 14px, transparent 14px)`,
      }}
      data-testid="dashboard-pinned-sticky"
    >
      <div className="flex items-center justify-between px-3 pt-2">
        <span className={`text-[10px] uppercase tracking-[0.08em] font-semibold ${c.accent}`}>Pinned Note</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={`p-0.5 rounded hover:bg-black/10 transition-colors ${c.accent}`} title="Choose which note is pinned" data-testid="pinned-sticky-picker">
              <Pin className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-border">
            <DropdownMenuItem className="text-xs" onClick={() => onSelect(null)} data-testid="pinned-sticky-auto">
              Auto (first note with content)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {stickies.map(s => (
              <DropdownMenuItem key={s.id} className="text-xs" onClick={() => onSelect(s.id)} data-testid={`pinned-sticky-option-${s.slot}`}>
                <span className="font-mono mr-1.5 text-muted-foreground">#{s.slot}</span>
                <span className="truncate max-w-[180px]">{(s.content || '').trim() ? (s.content || '').slice(0, 28) : 'Empty note'}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (draft !== (sticky.content || '')) onSave(sticky.id, draft); }}
        maxLength={280}
        placeholder="Jot something down..."
        className={`w-full h-[84px] bg-transparent resize-none px-3 pb-2 pt-1 text-[12px] leading-snug font-medium outline-none ${c.text} ${c.placeholder}`}
        data-testid="pinned-sticky-textarea"
      />
    </div>
  );
}
